import Sidebar from "./Components/Sidebar/Sidebar";
import NavigationWrapper from "./Components/Native/NavigationWrapper";
import NavSidebar from "./Components/Sidebar/NavSidebar";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import VisionBoardPage from "./Pages/VisionBoard";
import Home from "./Pages/Home";
import Goals from "./Pages/Goals";
import Milestones from "./Pages/Milestones";
import Wishlist from "./Pages/Wishlist";
import { YearProvider, AuthProvider, useAuthContext } from "./context/context";
import { LazyDataProvider } from "./context/LazyDataContext";
import Register from "./Pages/Register";
import Login from "./Pages/Login";
import Profile from "./Pages/Profile";
import PrivateRoute from "./Components/Elements/PrivateRoute";
import ForgotPassword from "./Pages/ForgotPassword";
import ForgotPasswordExpired from "./Pages/ForgotPasswordExpired";
import Notifications from "./Pages/Notifications";
import ResetPassword from "./Pages/ResetPassword";
import { LanguageProvider } from "./context/LanguageContext";
import RegisterResendLinkPage from "./Pages/RegisterResendLink";
import EmailChangeExpired from "./Pages/EmailChangeExpired";
import Pricing from "./Pages/Pricing";
import GoalDetails from "./Pages/GoalDetails";
import PublicWishlist from "./Pages/PublicWishlist";
import { useState, useEffect } from "react";
import { NotificationProvider } from "./context/NotificationContext";
import { ToastProvider } from "./context/ToastContext";
import Toast from "./Components/Elements/Toast";
import { useToast } from "./context/ToastContext";
import { VisionBoardProvider } from "./context/VisionBoardContext";
import Finance from "./Pages/Finance";
import { Capacitor } from '@capacitor/core';
import RevenueCatService from './services/revenuecat';
import PushNotificationService from './services/pushNotifications';

// Add this array of public routes at the top of the file, after imports
const PUBLIC_ROUTES = [
  "/login",
  "/register",
  "/forgot-password",
  "/forgot-password-expired",
  "/reset-password",
  "/register-resend-link",
  "/email-change-expired",
];

// Main app content component
function AppContent() {
  const { authStatus, userData } = useAuthContext();
  const location = useLocation();
  const [sidebarToggled, setSidebarToggled] = useState(false);
  const { toast, hideToast } = useToast();

  // Initialize RevenueCat and Push Notifications on native platforms
  useEffect(() => {
    const initNativeServices = async () => {
      // Only initialize on native platforms (iOS/Android)
      if (Capacitor.isNativePlatform()) {
        try {
          // Initialize RevenueCat
          await RevenueCatService.initialize();
          console.log('RevenueCat initialized successfully');

          // If user is logged in, set the user ID in RevenueCat
          if (userData?.id) {
            await RevenueCatService.setUser(`user_${userData.id}`);
            console.log('RevenueCat user ID set:', userData.id);
          }

          // Initialize Push Notifications if user is logged in
          if (userData?.id) {
            await PushNotificationService.initialize();
            console.log('Push notifications initialized successfully');
          }
        } catch (error) {
          console.error('Failed to initialize native services:', error);
        }
      }
    };

    initNativeServices();
  }, [userData?.id]);

  // Don't render anything while loading
  if (authStatus === "loading") {
    return null;
  }

  const isPublicRoute = PUBLIC_ROUTES.some(
    (route) =>
      location.pathname === route ||
      location.pathname.startsWith("/reset-password/") ||
      location.pathname.startsWith("/register-resend-link/")
  );

  // Hide sidebar on public wishlist page
  const isPublicWishlist = location.pathname.startsWith("/wishlist/public");

  return (
    <>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
      {!isPublicRoute && !isPublicWishlist && (
        <>
          <div className="hidden md:block">
            <Sidebar />
          </div>
          <div className="block md:hidden">
            <NavigationWrapper onFabClick={() => setSidebarToggled(true)} />
          </div>
          {sidebarToggled && (
            <NavSidebar setSidebarToggled={setSidebarToggled} />
          )}
        </>
      )}
      <div className="flex-1 min-w-0">
        <Routes>
        {/* Public Routes */}
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route
          path="/forgot-password-expired"
          element={<ForgotPasswordExpired />}
        />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route
          path="/register-resend-link/:token"
          element={<RegisterResendLinkPage />}
        />
        <Route path="/email-change-expired" element={<EmailChangeExpired />} />
        <Route
          path="/verify-email-change/:token"
          element={<Navigate to="/login" />}
        />
        <Route
          path="/wishlist/public/:year/:token"
          element={<PublicWishlist />}
        />

        {/* Private Routes */}
        <Route path="/" element={<PrivateRoute element={<Home />} />} />
        <Route
          path="/settings"
          element={<PrivateRoute element={<Profile />} />}
        />
        <Route
          path="/pricing"
          element={<PrivateRoute element={<Pricing />} />}
        />
        <Route
          path="/vision-board"
          element={<PrivateRoute element={<VisionBoardPage />} />}
        />
        <Route path="/goals" element={<PrivateRoute element={<Goals />} />} />
        <Route
          path="/calendar"
          element={<PrivateRoute element={<Milestones />} />}
        />
        <Route
          path="/wishlist"
          element={<PrivateRoute element={<Wishlist />} />}
        />
        <Route
          path="/notifications"
          element={<PrivateRoute element={<Notifications />} />}
        />
        <Route path="/finance" element={<PrivateRoute element={<Finance />} />} />
        <Route path="/goals/:goalId" element={<GoalDetails />} />
      </Routes>
      </div>
    </>
  );
}

function App() {
  return (
    <LazyDataProvider>
      <AuthProvider>
        <NotificationProvider>
          <YearProvider>
            <VisionBoardProvider>
              <LanguageProvider>
                <ToastProvider>
                  <AppContent />
                </ToastProvider>
              </LanguageProvider>
            </VisionBoardProvider>
          </YearProvider>
        </NotificationProvider>
      </AuthProvider>
    </LazyDataProvider>
  );
}

export default App;
